// Adapts the backend's named-field reference-data payload
// (GET /api/retirement-calculator/reference-data, RetirementReferenceDataDto)
// into the engine's positional-tuple `RetirementReferenceData` shape.
//
// The actuarial tables are validated before use — present, numeric, sorted,
// complete, and every table an assumption or tier points at actually exists.
// Any problem makes `fromReferenceDataDto` throw, and the caller keeps the
// bundled `defaultReferenceData` rather than letting the engine run on a
// partial set (the engine would otherwise silently read a missing mortality
// age as 0, or a missing table as "no factor").
//
// Help text and beneficiary-relationship options are UI copy, not math, so a
// problem there never rejects the payload: each item falls back to its
// bundled copy instead.
//
// Note: the shared axios interceptor rewrites every JSON null to '' before
// this runs, so nullable fields are checked by value, not by type.
import type {
  BeneficiaryRelationshipOption, ErfRow, HelpContent, Option1HistoricalRow, RapServiceAssumptionPeriod,
  RapServiceAssumptionRow, RetirementReferenceData, TierCfg,
} from './index';
import { BENEFICIARY_RELATIONSHIP_OPTIONS, HELP_CONTENT } from './uiContent';

export interface RetirementReferenceDataDto {
  tierConfig: Record<string, TierCfg & { oldFormula?: boolean | null | '' }>;
  membershipTierOptions: string[][];
  erfTables: Record<string, { age: number; factor: number }[]>;
  rapMortalityByDescription: Record<string, number[]>;
  rapOption1ByDescription: Record<string, { age: number; tableValue: number; levelIncome62?: number | null | ''; levelIncome65?: number | null | '' }[]>;
  rapServiceAssumptionPeriods: {
    effectiveFrom: string;
    effectiveTo: string;
    rows: {
      tier: string; minAge: number; interest: number;
      memberMortality: string; memberSetback: number;
      beneficiaryMortality: string; beneficiarySetback: number;
      option1Description: string; erfDescription: string;
    }[];
  }[];
  nonspouseSurvivorLimitPct: Record<string, number>;
  serviceCreditsPerYear: number;
  helpContent?: Record<string, { title: string; body: string[] }>;
  beneficiaryRelationshipOptions?: { code: string; label: string }[];
}

// The engine indexes mortality by whole age 0–115.
const MORTALITY_AGES = 116;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBlank = (v: unknown) => v === null || v === undefined || v === '';
const isText = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';
const entriesOf = <T>(o: Record<string, T> | undefined) => (o && typeof o === 'object' ? Object.entries(o) : []);
// The API sends dates as 'yyyy-MM-dd' (or 'yyyy-MM-ddTHH:mm:ss' for a
// DateTime); the engine compares plain 'yyyy-MM-dd' keys lexically.
const dateKey = (v: unknown) => {
  const s = String(v ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};

export function fromReferenceDataDto(dto: RetirementReferenceDataDto): RetirementReferenceData {
  const problems: string[] = [];
  const fail = (msg: string) => { problems.push(msg); };
  if (!dto || typeof dto !== 'object') throw new Error('Reference data payload is invalid: empty response');

  // ERF tables: numeric, strictly ascending by age.
  const erfTables: Record<string, readonly ErfRow[]> = {};
  for (const [name, rows] of entriesOf(dto.erfTables)) {
    const out: ErfRow[] = [];
    (Array.isArray(rows) ? rows : []).forEach((r, i) => {
      if (!isNum(r?.age) || !isNum(r?.factor)) fail(`ERF table "${name}" row ${i} is not numeric`);
      else if (out.length && r.age <= out[out.length - 1][0]) fail(`ERF table "${name}" is not in ascending age order`);
      else out.push([r.age, r.factor]);
    });
    if (!out.length) fail(`ERF table "${name}" is empty`);
    erfTables[name] = out;
  }
  if (!Object.keys(erfTables).length) fail('no ERF tables');

  // Mortality: one rate per age 0–115, each a probability.
  const rapMortalityByDescription: Record<string, readonly number[]> = {};
  for (const [name, rates] of entriesOf(dto.rapMortalityByDescription)) {
    if (!Array.isArray(rates) || rates.length < MORTALITY_AGES) fail(`mortality table "${name}" must have ${MORTALITY_AGES} ages`);
    else if (!rates.every(q => isNum(q) && q >= 0 && q <= 1)) fail(`mortality table "${name}" has a rate outside 0–1`);
    else rapMortalityByDescription[name] = rates;
  }
  if (!Object.keys(rapMortalityByDescription).length) fail('no mortality tables');

  // Option 1 / level-income: numeric, strictly ascending by age; level income may be blank.
  const rapOption1ByDescription: Record<string, readonly Option1HistoricalRow[]> = {};
  for (const [name, rows] of entriesOf(dto.rapOption1ByDescription)) {
    const out: Option1HistoricalRow[] = [];
    (Array.isArray(rows) ? rows : []).forEach((r, i) => {
      const li = [r?.levelIncome62, r?.levelIncome65];
      if (!isNum(r?.age) || !isNum(r?.tableValue) || li.some(v => !isBlank(v) && !isNum(v))) fail(`Option 1 table "${name}" row ${i} is not numeric`);
      else if (out.length && r.age <= out[out.length - 1][0]) fail(`Option 1 table "${name}" is not in ascending age order`);
      else out.push([r.age, r.tableValue, isBlank(li[0]) ? '' : String(li[0]), isBlank(li[1]) ? '' : String(li[1])]);
    });
    if (!out.length) fail(`Option 1 table "${name}" is empty`);
    rapOption1ByDescription[name] = out;
  }
  if (!Object.keys(rapOption1ByDescription).length) fail('no Option 1 tables');

  // Tiers: complete, and every tier's ERF table exists.
  const tierConfig: Record<string, TierCfg> = {};
  for (const [code, t] of entriesOf(dto.tierConfig)) {
    if (!isText(t?.name) || !isNum(t?.minAge) || !isText(t?.erf) || !isText(t?.mort) || !isText(t?.formula)) {
      fail(`tier ${code} is incomplete`);
      continue;
    }
    if (!erfTables[t.erf]) fail(`tier ${code} uses missing ERF table "${t.erf}"`);
    tierConfig[code] = { name: t.name, minAge: t.minAge, erf: t.erf, mort: t.mort, formula: t.formula, ...(t.oldFormula === true ? { oldFormula: true } : {}) };
  }
  if (!Object.keys(tierConfig).length) fail('no tiers');

  const membershipTierOptions = (Array.isArray(dto.membershipTierOptions) ? dto.membershipTierOptions : [])
    .map(o => [String(o?.[0] ?? ''), String(o?.[1] ?? '')] as const);
  if (!membershipTierOptions.length) fail('no selectable tiers');
  membershipTierOptions.forEach(([code, label]) => {
    if (!tierConfig[code] || !label.trim()) fail(`selectable tier "${code}" is not configured`);
  });

  // Assumption periods: valid, non-overlapping date ranges whose rows point at existing tables.
  const periods: RapServiceAssumptionPeriod[] = [];
  for (const p of Array.isArray(dto.rapServiceAssumptionPeriods) ? dto.rapServiceAssumptionPeriods : []) {
    const from = dateKey(p?.effectiveFrom), to = dateKey(p?.effectiveTo);
    if (!from || !to || from > to) { fail(`assumption period ${p?.effectiveFrom}–${p?.effectiveTo} has invalid dates`); continue; }
    const rows: RapServiceAssumptionRow[] = [];
    for (const r of Array.isArray(p.rows) ? p.rows : []) {
      const at = `assumption ${from} ${r?.tier}`;
      if (!isText(r?.tier) || !isNum(r?.minAge) || !isNum(r?.interest) || !isNum(r?.memberSetback) || !isNum(r?.beneficiarySetback)) { fail(`${at} is incomplete`); continue; }
      if (!rapMortalityByDescription[r.memberMortality]) fail(`${at} uses missing mortality table "${r.memberMortality}"`);
      if (!rapMortalityByDescription[r.beneficiaryMortality]) fail(`${at} uses missing mortality table "${r.beneficiaryMortality}"`);
      if (!rapOption1ByDescription[r.option1Description]) fail(`${at} uses missing Option 1 table "${r.option1Description}"`);
      if (!erfTables[r.erfDescription]) fail(`${at} uses missing ERF table "${r.erfDescription}"`);
      rows.push([r.tier, r.minAge, r.interest, r.memberMortality, r.memberSetback, r.beneficiaryMortality, r.beneficiarySetback, r.option1Description, r.erfDescription]);
    }
    if (!rows.length) fail(`assumption period ${from} has no rows`);
    periods.push([from, to, rows]);
  }
  if (!periods.length) fail('no assumption periods');
  const byStart = [...periods].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  for (let i = 1; i < byStart.length; i++) {
    if (byStart[i][0] <= byStart[i - 1][1]) fail(`assumption periods ${byStart[i - 1][0]} and ${byStart[i][0]} overlap`);
  }
  // Every selectable tier needs assumptions for at least one period.
  membershipTierOptions.forEach(([code]) => {
    if (!periods.some(([, , rows]) => rows.some(r => r[0] === code))) fail(`selectable tier "${code}" has no assumptions`);
  });

  const nonspouseSurvivorLimitPct: Record<number, number> = {};
  for (const [gap, pct] of entriesOf(dto.nonspouseSurvivorLimitPct)) {
    if (!/^\d+$/.test(gap) || !isNum(pct) || pct < 0 || pct > 100) fail(`nonspouse survivor limit for gap ${gap} is invalid`);
    else nonspouseSurvivorLimitPct[Number(gap)] = pct;
  }
  if (!Object.keys(nonspouseSurvivorLimitPct).length) fail('no nonspouse survivor limits');

  if (!isNum(dto.serviceCreditsPerYear) || dto.serviceCreditsPerYear <= 0) fail('service credits per year is missing');

  if (problems.length) {
    const more = problems.length > 3 ? `; and ${problems.length - 3} more` : '';
    throw new Error(`Reference data payload is invalid: ${problems.slice(0, 3).join('; ')}${more}`);
  }

  return {
    tierConfig,
    membershipTierOptions,
    erfTables,
    rapMortalityByDescription,
    rapOption1ByDescription,
    rapServiceAssumptionPeriods: periods,
    nonspouseSurvivorLimitPct,
    serviceCreditsPerYear: dto.serviceCreditsPerYear,
    helpContent: helpContentFrom(dto.helpContent),
    beneficiaryRelationshipOptions: beneficiaryOptionsFrom(dto.beneficiaryRelationshipOptions),
  };
}

// Server topics replace bundled ones key by key; a missing or malformed topic keeps the bundled copy.
function helpContentFrom(server: RetirementReferenceDataDto['helpContent']): Record<string, HelpContent> {
  const merged: Record<string, HelpContent> = { ...HELP_CONTENT };
  for (const [key, topic] of entriesOf(server)) {
    const body = Array.isArray(topic?.body) ? topic.body.filter(isText) : [];
    if (isText(topic?.title) && body.length) merged[key] = { title: topic.title, body };
  }
  return merged;
}

// Server labels and order for the codes the engine understands; unknown codes
// are dropped and any known code the server omits is kept from the bundle.
function beneficiaryOptionsFrom(server: RetirementReferenceDataDto['beneficiaryRelationshipOptions']): readonly BeneficiaryRelationshipOption[] {
  const known = new Set(BENEFICIARY_RELATIONSHIP_OPTIONS.map(o => o.code));
  const fromServer = (Array.isArray(server) ? server : [])
    .filter((o, i, all) => known.has(o?.code) && isText(o?.label) && all.findIndex(x => x?.code === o.code) === i)
    .map(o => ({ code: o.code, label: o.label }));
  return [...fromServer, ...BENEFICIARY_RELATIONSHIP_OPTIONS.filter(o => !fromServer.some(s => s.code === o.code))];
}
